import { motion } from "framer-motion";
import { Shield, Flag, AlertCircle, CheckCircle, XCircle, FileWarning } from "lucide-react";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";

interface ModerationProps {
  onNavigate?: (page: string) => void;
}

export function Moderation({ onNavigate }: ModerationProps) {
  const prohibitedContent = [
    {
      category: "Medical Claims",
      examples: [
        '"This cures cancer"',
        '"Guaranteed to treat depression"',
        '"Better than prescription medication"',
      ],
      why: "Unsubstantiated medical claims can be dangerous and violate healthcare regulations",
    },
    {
      category: "Dosage Advice",
      examples: [
        '"Take 50mg for best results"',
        '"I recommend starting with 2 grams"',
        '"Use this 3 times daily"',
      ],
      why: "Individual dosage varies greatly; only healthcare providers should give dosage advice",
    },
    {
      category: "Sales/Trading",
      examples: [
        '"Selling extra product, DM me"',
        '"Looking to buy this strain"',
        '"Trade for other products"',
      ],
      why: "Platform is for reviews only, not commerce. Illegal in many jurisdictions",
    },
  ];

  const blockedPhrases = [
    "cures", "treats", "heals", "guaranteed", "miracle",
    "take X mg", "dose", "recommended amount",
    "for sale", "selling", "buy from me", "trade",
  ];

  return (
    <div className="min-h-screen pt-32 pb-20 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <motion.div 
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-accent/50 border border-primary/20 mb-6">
            <Shield className="w-4 h-4 text-primary" />
            <span className="text-sm text-primary">Moderation Policy</span>
          </div>
          <h1 className="text-4xl sm:text-5xl mb-6">
            Moderation &
            <span className="block text-primary">Community Guidelines</span>
          </h1>
          <p className="text-xl text-muted-foreground mb-4">
            Keeping our community safe, legal, and helpful
          </p>
        </motion.div>

        {/* Quick Overview */}
        <motion.div 
          className="grid md:grid-cols-3 gap-6 mb-16"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1, duration: 0.6 }}
        >
          <Card className="p-6 text-center">
            <Shield className="w-8 h-8 text-primary mx-auto mb-4" />
            <h3 className="mb-2">Patient Safety</h3>
            <p className="text-sm text-muted-foreground">Protecting users from harmful misinformation</p>
          </Card>
          <Card className="p-6 text-center">
            <CheckCircle className="w-8 h-8 text-primary mx-auto mb-4" />
            <h3 className="mb-2">Legal Compliance</h3>
            <p className="text-sm text-muted-foreground">Ensuring platform adheres to regulations</p>
          </Card>
          <Card className="p-6 text-center">
            <Flag className="w-8 h-8 text-primary mx-auto mb-4" />
            <h3 className="mb-2">24/7 Monitoring</h3>
            <p className="text-sm text-muted-foreground">Healthcare professionals moderate content</p>
          </Card>
        </motion.div>

        {/* Content */}
        <motion.div 
          className="prose prose-lg max-w-none space-y-12"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.6 }}
        >
          <section className="space-y-4">
            <h2 className="text-2xl">Our Moderation Philosophy</h2>
            <p className="text-muted-foreground leading-relaxed">
              CannaBias exists to help patients share their experiences with cannabis products in a safe, legal, and helpful way. Our moderation approach balances:
            </p>
            <ul className="space-y-2 text-muted-foreground ml-6">
              <li>• <strong>Patient Safety:</strong> Preventing harmful medical misinformation</li>
              <li>• <strong>Legal Compliance:</strong> Adhering to healthcare and cannabis regulations</li>
              <li>• <strong>Free Expression:</strong> Allowing honest product feedback</li>
              <li>• <strong>Community Trust:</strong> Maintaining a respectful environment</li>
            </ul>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl flex items-center gap-3">
              <XCircle className="w-6 h-6 text-destructive" />
              Prohibited Content
            </h2>
            <p className="text-muted-foreground leading-relaxed">
              The following types of content are <strong>strictly prohibited</strong> and will be removed:
            </p>

            {prohibitedContent.map((item, index) => (
              <div key={index} className="p-6 rounded-2xl bg-muted/30 border border-border">
                <div className="flex items-start gap-3 mb-4">
                  <AlertCircle className="w-5 h-5 text-destructive flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="text-xl mb-2">{item.category}</h3>
                    <p className="text-sm text-muted-foreground mb-4">
                      <strong>Why prohibited:</strong> {item.why}
                    </p>
                  </div>
                </div>
                <div className="pl-8">
                  <p className="text-sm mb-2">Examples of prohibited statements:</p>
                  <ul className="space-y-2 text-sm text-muted-foreground">
                    {item.examples.map((example, i) => (
                      <li key={i} className="flex items-start gap-2">
                        <XCircle className="w-4 h-4 text-destructive flex-shrink-0 mt-0.5" />
                        <span>{example}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl flex items-center gap-3">
              <FileWarning className="w-6 h-6 text-primary" />
              Automated Content Filtering
            </h2>
            <p className="text-muted-foreground leading-relaxed">
              We use automated systems to detect prohibited content before it's published. Reviews containing certain phrases are automatically flagged for manual review.
            </p>
            
            <div className="p-6 rounded-2xl bg-accent/30 border border-primary/20">
              <h3 className="text-lg mb-4">Blocked Phrases Include:</h3>
              <div className="flex flex-wrap gap-2">
                {blockedPhrases.map((phrase, index) => (
                  <Badge key={index} variant="secondary">
                    "{phrase}"
                  </Badge>
                ))}
              </div>
              <p className="text-sm text-muted-foreground mt-4">
                If your review contains these phrases, it will be held for manual review by our moderation team.
              </p>
            </div>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl flex items-center gap-3">
              <CheckCircle className="w-6 h-6 text-primary" />
              What You CAN Review
            </h2>
            <p className="text-muted-foreground leading-relaxed">
              We encourage honest, detailed reviews focusing on:
            </p>
            <div className="grid md:grid-cols-2 gap-4">
              <Card className="p-6 bg-primary/5">
                <h4 className="mb-3">✓ Product Quality</h4>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li>• Appearance and aroma</li>
                  <li>• Freshness and moisture</li>
                  <li>• Overall quality compared to expectations</li>
                </ul>
              </Card>
              <Card className="p-6 bg-primary/5">
                <h4 className="mb-3">✓ Consistency</h4>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li>• Batch-to-batch consistency</li>
                  <li>• Comparison to previous purchases</li>
                  <li>• Reliability of the product</li>
                </ul>
              </Card>
              <Card className="p-6 bg-primary/5">
                <h4 className="mb-3">✓ Packaging</h4>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li>• Quality of container/packaging</li>
                  <li>• Freshness seals and preservation</li>
                  <li>• Labeling accuracy</li>
                </ul>
              </Card>
              <Card className="p-6 bg-primary/5">
                <h4 className="mb-3">✓ Availability</h4>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li>• Stock consistency at clinic</li>
                  <li>• Wait times and accessibility</li>
                  <li>• Customer service experience</li>
                </ul>
              </Card>
            </div>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl">Moderation Process</h2>
            
            <div className="space-y-4">
              <h3 className="text-xl">1. Automated Filtering</h3>
              <p className="text-muted-foreground leading-relaxed">
                All submitted reviews are scanned for prohibited phrases and content patterns. Flagged reviews are held for manual review.
              </p>
            </div>

            <div className="space-y-4">
              <h3 className="text-xl">2. Healthcare Professional Review</h3>
              <p className="text-muted-foreground leading-relaxed">
                Flagged content is reviewed by licensed healthcare professionals within 2 hours. They assess whether content:
              </p>
              <ul className="space-y-2 text-muted-foreground ml-6">
                <li>• Contains medical claims</li>
                <li>• Provides specific dosage advice</li>
                <li>• Complies with community guidelines</li>
              </ul>
            </div>

            <div className="space-y-4">
              <h3 className="text-xl">3. User Flagging</h3>
              <p className="text-muted-foreground leading-relaxed">
                Community members can flag inappropriate content. All flags are reviewed by moderators within 24 hours.
              </p>
            </div>

            <div className="space-y-4">
              <h3 className="text-xl">4. Moderation Actions</h3>
              <p className="text-muted-foreground leading-relaxed">
                Based on review severity, we may:
              </p>
              <ul className="space-y-2 text-muted-foreground ml-6">
                <li>• <strong>Approve:</strong> Publish the review</li>
                <li>• <strong>Request Edit:</strong> Ask author to remove prohibited content</li>
                <li>• <strong>Remove:</strong> Delete content that violates guidelines</li>
                <li>• <strong>Suspend Account:</strong> For repeated violations</li>
              </ul>
            </div>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl">Patient Responsibilities</h2>
            <p className="text-muted-foreground leading-relaxed">
              As a patient reviewer, you are responsible for:
            </p>
            <ul className="space-y-2 text-muted-foreground ml-6">
              <li>• Providing honest, accurate reviews based on personal experience</li>
              <li>• Avoiding medical claims or dosage recommendations</li>
              <li>• Respecting other community members</li>
              <li>• Understanding that reviews reflect personal experience, not medical advice</li>
              <li>• Reporting content that violates our guidelines</li>
            </ul>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl">Appeals Process</h2>
            <p className="text-muted-foreground leading-relaxed">
              If your content was removed and you believe it was a mistake:
            </p>
            <ol className="space-y-2 text-muted-foreground ml-6">
              <li>1. You'll receive an email explaining why content was removed</li>
              <li>2. Reply to that email with your appeal and explanation</li>
              <li>3. A different moderator will review your appeal within 48 hours</li>
              <li>4. You'll receive a final decision with detailed reasoning</li>
            </ol>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl flex items-center gap-3">
              <Shield className="w-6 h-6 text-primary" />
              Transparency & Accountability
            </h2>
            <p className="text-muted-foreground leading-relaxed">
              We publish quarterly{" "}
              <button 
                onClick={() => onNavigate && onNavigate("transparency")}
                className="text-primary hover:underline"
              >
                Transparency Reports
              </button>
              {" "}detailing:
            </p>
            <ul className="space-y-2 text-muted-foreground ml-6">
              <li>• Number of reviews submitted vs. published</li>
              <li>• Removal statistics by category</li>
              <li>• User flags received and actions taken</li>
              <li>• Account suspensions</li>
              <li>• Appeals filed and outcomes</li>
            </ul>
            <p className="text-muted-foreground leading-relaxed">
              All moderation actions are logged in an immutable audit trail for regulatory compliance and accountability.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl">Contact Moderation Team</h2>
            <p className="text-muted-foreground leading-relaxed">
              Questions about our moderation policy or specific moderation decisions?
            </p>
            <div className="p-6 rounded-2xl bg-muted/30 border border-border">
              <p className="text-muted-foreground mb-2"><strong>Email:</strong> moderation@cannabias.tech</p>
              <p className="text-muted-foreground">Response time: Within 24 hours</p>
            </div>
          </section>

          <div className="p-6 rounded-2xl bg-primary/10 border border-primary/20 flex items-start gap-4">
            <Shield className="w-6 h-6 text-primary flex-shrink-0 mt-1" />
            <div>
              <h3 className="text-lg mb-2">Our Commitment</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                We are committed to maintaining a safe, legal, and helpful platform for all patients. Our moderation practices are designed to protect you while allowing honest product feedback. Thank you for helping us build a trustworthy community.
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
